import type { NestedActionView, StagedActionViewTag } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

/** Nested-view type the backend uses to ship an update action's prior state. */
export const BEFORE_UPDATE_TYPE = 'beforeUpdate'
export const PREREQUISITE_UNSATISFIED_TYPE = 'prerequisiteUnsatisfied'

/**
 * Nested views that should render as their own blocks. The `beforeUpdate` view is
 * data for the transition badges (see `applyMetadataTransitions`), not a block to
 * display — surfacing it too would duplicate the prior values.
 */
export function visibleNestedViews<T extends { type?: string }>(nestedViews: Array<T>): Array<T> {
  return nestedViews.filter(
    (nested) => nested.type !== BEFORE_UPDATE_TYPE && nested.type !== PREREQUISITE_UNSATISFIED_TYPE,
  )
}

/**
 * Folds an update action's prior values into its tags so each badge can render the
 * transition (`~~previous~~ → new`) without the rest of the tag pipeline needing to
 * know about nested views.
 *
 * The backend ships the before-state as a `beforeUpdate` nested view. We match its
 * tags to the current tags by key and stash the prior value/icons as
 * `previousValue`/`previousIcon` on every key whose value actually changed — so this
 * works for Status, Priority, or any other metadata key, not just one.
 *
 * A `prev → new` transition only makes sense for single-valued keys, so we skip any
 * key with more than one tag before or after (e.g. labels): a multi-value key has no
 * clean prior↔new pairing and would otherwise stamp the wrong (or a phantom) prior
 * value onto its badges.
 *
 * Returns the original array untouched when nothing changed, so create actions and
 * non-update edits pay nothing.
 */
export function applyMetadataTransitions(
  tags: Array<StagedActionViewTag>,
  nestedViews?: Array<NestedActionView>,
): Array<StagedActionViewTag> {
  if (!nestedViews?.length) return tags

  const previousTags = nestedViews.find((nested) => nested.type === BEFORE_UPDATE_TYPE)?.view.tags
  if (!previousTags?.length) return tags

  const countByKey = (list: Array<StagedActionViewTag>) => {
    const counts = new Map<string, number>()
    for (const tag of list) counts.set(tag.key, (counts.get(tag.key) ?? 0) + 1)
    return counts
  }
  const currentCounts = countByKey(tags)
  const previousCounts = countByKey(previousTags)
  const previousByKey = new Map(previousTags.map((tag) => [tag.key, tag]))

  let changed = false
  const next = tags.map((current) => {
    if (currentCounts.get(current.key) !== 1 || previousCounts.get(current.key) !== 1) return current
    const previous = previousByKey.get(current.key)
    if (!previous?.value || previous.value === current.value) return current
    changed = true
    return {
      ...current,
      previousValue: previous.value,
      previousIcon: previous.icon,
      previousDarkModeIcon: previous.darkModeIcon,
    }
  })

  return changed ? next : tags
}
